import React, { useState } from 'react'
import { View, StyleSheet, Platform, Modal, Linking } from 'react-native'
import { Layout, Text, Button, Icon, useTheme } from '@ui-kitten/components'
import * as Updates from 'expo-updates'
import { useTranslation } from 'react-i18next'
import { usePostHog } from 'posthog-react-native'
import { APP_VERSION, storeUrlForPlatform } from '../hooks/useVersionCheck'
import SupportChatPanel from '../screens/Support/SupportChatPanel'
import { shouldUseNativeSupportChat } from '../global/Support/supportFlag'

export default function BlockedScreen({ config }) {
  const [isChecking, setIsChecking] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const { t } = useTranslation()
  const theme = useTheme()
  const postHog = usePostHog()
  const storeUrl = storeUrlForPlatform(config)

  /**
   * A hard lockout is only safe to ramp if you can see whether the people it blocks get OUT,
   * and by which route. `version_lockout_shown` counts who is stuck; this counts who escaped.
   * The `ota_unavailable` bucket is the one that matters most — it is a device the OTA channel
   * will never serve again, i.e. one that needs a native store install or it is stranded.
   */
  const reportRecovery = (method) =>
    postHog?.capture?.('version_lockout_recovery', {
      method,
      lockout_type: 'hard',
      min_version: config?.minVersion ?? null,
      app_version: APP_VERSION,
    })

  // No floating button on this screen, so host the chat panel in its own modal.
  // If native chat is disabled via the kill-switch (or unavailable this early in
  // launch), fall back to the web help center.
  const openSupport = () => {
    reportRecovery('support_opened')
    if (shouldUseNativeSupportChat()) {
      setChatOpen(true)
      return
    }
    Linking.openURL('https://web.atlas-app.org').catch(() => {})
  }

  const handleCheckUpdate = async () => {
    if (Platform.OS === 'web') {
      // On web, just reload the page
      reportRecovery('web_reload')
      window.location.reload()
      return
    }

    setIsChecking(true)
    try {
      const update = await Updates.checkForUpdateAsync()
      if (update.isAvailable) {
        await Updates.fetchUpdateAsync()
        reportRecovery('ota_applied')
        // reloadAsync tears down the JS process, so the batched event has to go out first.
        // Promise.resolve() because flush() is absent on some client builds and must not throw.
        await Promise.resolve(postHog?.flush?.()).catch(() => {})
        await Updates.reloadAsync()
      } else {
        // ATL-791: for a runtime the OTA channel no longer serves (never-OTA 2.7/3.0/4.0
        // bundles), "no update available" is PERMANENT — this alert used to be the screen's
        // dead end. Point at the store install instead of "try again later".
        reportRecovery('ota_unavailable')
        alert(t('blockedScreen.noUpdateAvailable'))
      }
    } catch (error) {
      console.error('Failed to check for updates:', error)
      reportRecovery('ota_failed')
      alert(t('blockedScreen.updateFailed'))
    }
    setIsChecking(false)
  }

  const handleOpenStore = () => {
    if (!storeUrl) return
    reportRecovery('store_opened')
    // On a HARD-blocked screen this is the user's only recovery path — a silent failure
    // would strand them, so say something they can act on (review: finding 6).
    Linking.openURL(storeUrl).catch((error) => {
      console.error('Failed to open store listing:', error)
      alert(t('blockedScreen.updateFailed'))
    })
  }

  return (
    <Layout style={styles.container}>
      <View style={styles.content}>
        <Icon
          name="refresh-outline"
          style={styles.icon}
          fill={theme['color-primary-500']}
        />

        <Text category="h4" style={styles.title}>
          {t('blockedScreen.title')}
        </Text>

        <Text category="p1" style={styles.message}>
          {config?.lockoutMessage || t('blockedScreen.message')}
        </Text>

        <Text category="c1" appearance="hint" style={styles.hint}>
          {t('blockedScreen.hint')}
        </Text>

        <Text category="c2" appearance="hint" style={styles.version}>
          {t('blockedScreen.currentVersion')}: {APP_VERSION}
        </Text>
        {config?.minVersion && (
          <Text category="c2" appearance="hint" style={styles.version}>
            {t('blockedScreen.requiredVersion')}: {config.minVersion}
          </Text>
        )}

        <Button
          style={styles.button}
          size="large"
          onPress={handleCheckUpdate}
          disabled={isChecking}
        >
          {isChecking ? t('blockedScreen.checking') : t('blockedScreen.checkForUpdate')}
        </Button>

        {storeUrl && (
          // ATL-791: the OTA button above dead-ends never-OTA runtimes ("no update
          // available", forever). The store install is the only recovery path for them,
          // so it is always offered on native — harmless on OTA-capable devices.
          <Button
            style={styles.supportButton}
            size="large"
            status="basic"
            onPress={handleOpenStore}
          >
            {Platform.OS === 'ios'
              ? t('blockedScreen.openAppStore')
              : t('blockedScreen.openPlayStore')}
          </Button>
        )}

        <Button
          style={styles.supportButton}
          size="large"
          status="basic"
          onPress={openSupport}
        >
          {t('blockedScreen.contactSupport')}
        </Button>
      </View>

      <Modal
        visible={chatOpen}
        animationType="slide"
        onRequestClose={() => setChatOpen(false)}
        presentationStyle={Platform.OS !== 'web' ? 'pageSheet' : undefined}
        transparent={false}
      >
        <SupportChatPanel onBack={() => setChatOpen(false)} />
      </Modal>
    </Layout>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  icon: {
    width: 80,
    height: 80,
    marginBottom: 24,
  },
  title: {
    textAlign: 'center',
    marginBottom: 12,
  },
  message: {
    textAlign: 'center',
    marginBottom: 16,
    paddingHorizontal: 16,
  },
  hint: {
    textAlign: 'center',
    marginBottom: 24,
    paddingHorizontal: 16,
  },
  version: {
    textAlign: 'center',
    marginBottom: 4,
  },
  button: {
    minWidth: 200,
    marginTop: 16,
  },
  supportButton: {
    minWidth: 200,
    marginTop: 12,
  },
})
